import React, { createContext, use, useCallback, useEffect, useMemo, useState } from 'react';
import { useLocalStorage } from 'usehooks-ts';

import { debounce } from '../helpers/debounce';
import { toggleAttribute } from '../helpers/layout';

const INIT_STATE = {
  skin: 'minimal',
  theme: 'light',
  orientation: 'vertical',
  sidenav: {
    size: 'default',
    color: 'dark',
    user: true,
    isMobileMenuOpen: false,
  },
  topBar: {
    color: 'gray',
  },
  position: 'fixed',
  width: 'fluid',
};

const LayoutContext = createContext(undefined);

const useLayoutContext = () => {
  const context = use(LayoutContext);
  if (!context) {
    throw new Error('useLayoutContext can only be used within LayoutProvider');
  }
  return context;
};

function LayoutProvider({ children }) {
  const [settings, setSettings] = useLocalStorage('__LAYOUT_CONFIG__', INIT_STATE);

  const [offcanvasStates, setOffcanvasStates] = useState({
    showCustomizer: false,
  });

  // update settings
  const updateSettings = useCallback(
    (_newSettings) => {
      setSettings((prevSettings) => ({
        ...prevSettings,
        ..._newSettings,
        sidenav: {
          ...prevSettings.sidenav,
          ...(_newSettings.sidenav || {}),
        },
        topBar: {
          ...prevSettings.topBar,
          ...(_newSettings.topBar || {}),
        },
      }));
    },
    [setSettings],
  );

  const changeTheme = useCallback(
    (nTheme, persist = true) => {
      toggleAttribute('data-bs-theme', nTheme);
      if (persist) updateSettings({ theme: nTheme });
    },
    [updateSettings],
  );

  const changeSideNavSize = useCallback(
    (nSize, persist = true) => {
      toggleAttribute('data-sidenav-size', nSize);
      if (persist) updateSettings({ sidenav: { ...settings.sidenav, size: nSize } });
    },
    [settings.sidenav, updateSettings],
  );

  const toggleMobileMenu = () => {
    updateSettings({
      sidenav: { ...settings.sidenav, isMobileMenuOpen: !settings.sidenav.isMobileMenuOpen },
    });
  };

  const changeLayoutPosition = useCallback(
    (nPosition, persist = true) => {
      toggleAttribute('data-layout-position', nPosition);
      if (persist) updateSettings({ position: nPosition });
    },
    [updateSettings],
  );

  const changeLayoutWidth = useCallback(
    (nWidth, persist = true) => {
      toggleAttribute('data-layout-width', nWidth);
      if (persist) updateSettings({ width: nWidth });
    },
    [updateSettings],
  );

  const toggleCustomizer = () => {
    setOffcanvasStates({
      ...offcanvasStates,
      showCustomizer: !offcanvasStates.showCustomizer,
    });
  };

  const customizer = {
    isOpen: offcanvasStates.showCustomizer,
    toggle: toggleCustomizer,
  };

  const reset = useCallback(() => {
    setSettings(INIT_STATE);
  }, [setSettings]);

  const showBackdrop = () => {
    const backdrop = document.createElement('div');
    backdrop.id = 'custom-backdrop';
    backdrop.className = 'offcanvas-backdrop fade show';
    document.body.appendChild(backdrop);
    document.body.style.overflow = 'hidden';
    if (window.innerWidth > 767) {
      document.body.style.paddingRight = '15px';
    }
    backdrop.addEventListener('click', () => {
      const html = document.documentElement;
      html.classList.remove('sidebar-enable');
      hideBackdrop();
    });
  };

  const hideBackdrop = () => {
    const backdrop = document.getElementById('custom-backdrop');
    if (backdrop) {
      document.body.removeChild(backdrop);
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
    }
  };

  useEffect(() => {
    const getSystemTheme = () =>
      window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';

    toggleAttribute('data-skin', settings.skin);
    toggleAttribute(
      'data-bs-theme',
      settings.theme === 'system' ? getSystemTheme() : settings.theme,
    );
    toggleAttribute('data-topbar-color', settings.topBar.color);
    toggleAttribute('data-menu-color', settings.sidenav.color);
    toggleAttribute('data-sidenav-size', settings.sidenav.size);
    toggleAttribute('data-sidenav-user', settings.sidenav.user.toString());
    toggleAttribute('data-layout-position', settings.position);
    toggleAttribute('data-layout-width', settings.width);
  }, [settings]);

  const [hasHydrated, setHasHydrated] = useState(false);

  useEffect(() => {
    setHasHydrated(true);
  }, []);

  useEffect(() => {
    if (!hasHydrated) return;

    const handleResize = () => {
      const width = window.innerWidth;

      if (settings.orientation === 'vertical') {
        if (width <= 767.98) {
          changeSideNavSize('offcanvas', false);
        } else if (width <= 1140 && settings.sidenav.size !== 'offcanvas') {
          changeSideNavSize(
            settings.sidenav.size === 'on-hover' ? 'condensed' : 'condensed',
            false,
          );
        } else {
          changeSideNavSize(settings.sidenav.size);
        }
      } else if (settings.orientation === 'horizontal') {
        if (width < 992) {
          changeSideNavSize('offcanvas');
        } else {
          changeSideNavSize('default');
        }
      }
    };

    handleResize();

    const debouncedResize = debounce(handleResize, 200);

    window.addEventListener('resize', debouncedResize);

    // eslint-disable-next-line consistent-return
    return () => {
      window.removeEventListener('resize', debouncedResize);
    };
  }, [hasHydrated, settings.orientation, settings.sidenav.size]);

  return (
    <LayoutContext.Provider
      value={useMemo(
        () => ({
          ...settings,
          changeTheme,
          changeSideNavSize,
          toggleMobileMenu,
          changeLayoutPosition,
          changeLayoutWidth,
          customizer,
          reset,
          showBackdrop,
          hideBackdrop,
        }),
        [
          settings,
          changeTheme,
          changeSideNavSize,
          changeLayoutPosition,
          changeLayoutWidth,
          customizer,
        ],
      )}
    >
      {children}
    </LayoutContext.Provider>
  );
}
export { LayoutProvider, useLayoutContext };
